import { getFirestore, doc, collection, query, where, addDoc, getDoc, getDocs, updateDoc, setDoc, onSnapshot, deleteDoc, documentId, arrayUnion, arrayRemove, runTransaction } from "firebase/firestore"
import getCollectionPath from '@/assets/functions/getCollectionPath'
import lazyListener from '@/assets/functions/lazyListener'

const db = getFirestore()
const collectionName = 'userData/'

function docToInstance(doc) {
  const data = doc.data()
  return data ? new UserData(doc.id, data.user, data.activities, data.notifs, data.lastCheck) : null
}

export default class UserData {
  constructor(id, user, activities, notifs, lastCheck) {
    this.id = id
    this.user = user
    this.activities = activities
    this.notifs = notifs
    this.lastCheck = lastCheck
  }

  static initOne(user){
    let tmp_activities = {
      phone: [],
      mail: [],
      files: [],
      agenda: [],
      tasks: [],
    }

    let data = new UserData(null, user, tmp_activities, [], 0)
    return data
  }
    
  static async getNewId() {
    const newDocRef = doc(collection(db, await getCollectionPath(collectionName)))
    return newDocRef.id
  }

  async save() {
    const new_data = {
      user: this.user,
      activities: this.activities,
      notifs: this.notifs,
      lastCheck: this.lastCheck
    }

    if (this.id) {
      // Les notifications ne sont PAS réécrites ici : elles peuvent être ajoutées en parallèle
      // par d'autres utilisateurs. Elles passent uniquement par addNotif / removeNotifs / clearNotifs.
      delete new_data.notifs
      await updateDoc(doc(db, await getCollectionPath(collectionName), this.id), new_data)
    } else {
      const res = await addDoc(collection(db, await getCollectionPath(collectionName)), new_data)
      this.id = res.id
    }
  }

  // ---- Notifications : mises à jour atomiques ----
  // Chaque modification passe par une transaction Firestore : on relit les notifications
  // du document au moment de l'écriture, on applique le changement, puis on réécrit le tableau.
  // Rien n'est donc perdu si un autre utilisateur (ou un autre onglet) écrit en même temps,
  // et on ne dépend pas d'une comparaison exacte d'objets (arrayRemove).

  async updateNotifs(transform) {
    const docRef = doc(db, await getCollectionPath(collectionName), this.id)
    const notifs = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(docRef)
      if (!snapshot.exists()) throw new Error('Document userData introuvable : ' + this.id)
      const current = (snapshot.data().notifs || []).map(n => ({ ...n }))
      const updated = transform(current).map(n => JSON.parse(JSON.stringify(n)))
      transaction.update(docRef, { notifs: updated })
      return updated
    })
    // Mise à jour locale immédiate (le listener Firestore confirmera)
    this.notifs = notifs
    return notifs
  }

  // Ajoute une notification en remplaçant celles qui correspondent à replacePredicate (même élément, même type…)
  async addNotif(notif, replacePredicate = null) {
    const plainNotif = JSON.parse(JSON.stringify(notif))
    return this.updateNotifs(notifs => {
      const kept = replacePredicate ? notifs.filter(n => !replacePredicate(n)) : notifs
      return [...kept, plainNotif]
    })
  }

  async removeNotifs(predicate) {
    if (!(this.notifs || []).some(predicate)) return
    return this.updateNotifs(notifs => notifs.filter(n => !predicate(n)))
  }

  async clearNotifs() {
    return this.updateNotifs(() => [])
  }

  static listenById(id, callback) {
    return lazyListener(async (onSnapshot) => {
      return onSnapshot(doc(db, await getCollectionPath(collectionName), id), snapshot => {
        callback(docToInstance(snapshot))
      })
    })
  }

  static listenByUser(userId, callback) {
    return lazyListener(async (onSnapshot) => {
      return onSnapshot(query(collection(db, await getCollectionPath(collectionName)), where("user", "==", userId)), snapshot => {
        const list = []
        snapshot.forEach(doc => {
          list.push(docToInstance(doc))
        })
        callback(list)
      })
    })
  }

  static listenAll(callback) {
    return lazyListener(async (onSnapshot) => {
      onSnapshot(collection(db, await getCollectionPath(collectionName)), snapshot => {
        const list = []
        snapshot.forEach(doc => {
          list.push(docToInstance(doc))
        })
        callback(list)
      })
    })
  }
  static async getById(id) {
    let document = await getDoc(doc(db, collectionName, id))
    return docToInstance(document)
  }

  static async getByUser(userId,){
    let response = await getDocs(query(collection(db, await getCollectionPath(collectionName)), where("user", "==", userId)))
    let list = []
    response.forEach(doc => {
      list.push(docToInstance(doc))
    })
    return list
  }

  static async getAll(){
    let response = await getDocs(collection(db, await getCollectionPath(collectionName)))
    let list = []
    response.forEach(doc => {
        list.push(docToInstance(doc))
    })
    return list
}

  async delete(){
      let documentRef = doc(db, await getCollectionPath(collectionName), this.id)
      deleteDoc(documentRef)
  }

}
